import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";
import "./StdLibrary.css";

function StdLibrary() {
  const navigate = useNavigate();
  const [libraryRows, setLibraryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totalBorrowed = libraryRows.length;
  const currentlyBorrowed = libraryRows.filter(
    (row) => !row.return_date || row.return_date === "-"
  ).length;
  const returnedCount = libraryRows.filter(
    (row) => row.return_date && row.return_date !== "-"
  ).length;
  const overdueCount = libraryRows.filter((row) => {
    if (row.return_date && row.return_date !== "-") return false;
    if (!row.due_date || row.due_date === "-") return false;
    const dueDate = new Date(row.due_date);
    const today = new Date();
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  }).length;

  useEffect(() => {
    const fetchStudentLibrary = async () => {
      const studentId = sessionStorage.getItem("userId");
      const academicYearId = localStorage.getItem("academicSessionId") || "";

      if (!studentId) {
        setError("Student session details are missing. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const queryParams = new URLSearchParams();
        if (academicYearId) {
          queryParams.append("academic_year_id", academicYearId);
        }

        const response = await fetch(
          `${ApiUrl.apiurl}STUDENT_LIBRARY/GetStudentLibraryDetails/${studentId}/?${queryParams.toString()}`
        );

        const contentType = response.headers.get("content-type") || "";
        const payload = contentType.includes("application/json")
          ? await response.json()
          : null;

        if (!response.ok || !payload || payload.message !== "Success") {
          throw new Error(payload?.message || "Unable to load library details.");
        }

        setLibraryRows(Array.isArray(payload.data) ? payload.data : []);
      } catch (fetchError) {
        console.error("Error fetching student library details:", fetchError);
        setLibraryRows([]);
        setError(fetchError.message || "Unable to load library details.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentLibrary();
  }, []);

  return (
    <div className="container-fluid student-library-page">
      <div className="row">
        <div className="col-12">
          <div className="student-library-shell">
            <div className="student-library-header">
              <button
                type="button"
                className="btn btn-danger student-library-close"
                onClick={() => navigate("/student/dashboards")}
              >
                Close
              </button>
              <h2 className="student-library-title">MY LIBRARY</h2>
            </div>

            {loading ? (
              <div className="text-center py-4">Loading library details...</div>
            ) : error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : (
              <>
                {libraryRows.length === 0 ? (
                  <div className="student-library-alert">No Record Found!</div>
                ) : null}

                <div className="student-library-stats">
                  <div className="student-library-stat-card stat-borrowed">
                    <div className="student-library-stat-icon">📚</div>
                    <div className="student-library-stat-value">{totalBorrowed}</div>
                    <div className="student-library-stat-label">Total Borrowed</div>
                  </div>

                  <div className="student-library-stat-card stat-current">
                    <div className="student-library-stat-icon">📖</div>
                    <div className="student-library-stat-value">{currentlyBorrowed}</div>
                    <div className="student-library-stat-label">Currently Borrowed</div>
                  </div>

                  <div className="student-library-stat-card stat-returned">
                    <div className="student-library-stat-icon">✅</div>
                    <div className="student-library-stat-value">{returnedCount}</div>
                    <div className="student-library-stat-label">Returned</div>
                  </div>

                  <div className="student-library-stat-card stat-overdue">
                    <div className="student-library-stat-icon">⚠️</div>
                    <div className="student-library-stat-value">{overdueCount}</div>
                    <div className="student-library-stat-label">Overdue</div>
                  </div>
                </div>

                <div className="student-library-history-title">Book History</div>
                <div className="student-library-history-card">
                  {libraryRows.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Sr No</th>
                            <th>Books</th>
                            <th>Book Accession No</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Return Date</th>
                            <th>Penalty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {libraryRows.map((row) => (
                            <tr key={row.book_issue_id || row.sr_no}>
                              <td>{row.sr_no}</td>
                              <td>{row.book_name || "-"}</td>
                              <td>{row.book_accession_no || "-"}</td>
                              <td>{row.issue_date || "-"}</td>
                              <td>{row.due_date || "-"}</td>
                              <td>{row.return_date || "-"}</td>
                              <td>{`Rs. ${row.penalty ?? 0}`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="student-library-empty-state">
                      <div className="student-library-empty-icon">📖</div>
                      <div className="student-library-empty-title">No books borrowed yet</div>
                      <div className="student-library-empty-text">
                        Visit your library to borrow books!
                      </div>
                    </div>
                  )}
                </div>

                <div className="student-library-guidelines">
                  <div className="student-library-guidelines-title">
                    Library Guidelines
                  </div>
                  <ul className="student-library-guidelines-list">
                    <li>Books must be returned within the due date to avoid penalties.</li>
                    <li>Handle books with care and avoid marking or damaging them.</li>
                    <li>Lost or damaged books must be replaced or compensated.</li>
                    <li>Contact the library staff for any queries or assistance.</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StdLibrary;
